/**
 * @namespace ThemeEvents
 * @description A collection of theme-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new VariantUpdateEvent(variant, sectionId, { html }));
 * document.addEventListener(ThemeEvents.variantUpdate, (e) => { handle variant update });
 */
export class ThemeEvents {
  /** @static @constant {string} Event triggered when a variant is selected */
  static variantSelected = 'variant:selected';
  /** @static @constant {string} Event triggered when a variant is changed */
  static variantUpdate = 'variant:update';
  /** @static @constant {string} Event triggered when the cart items or quantities are updated */
  static cartUpdate = 'cart:update';
  /** @static @constant {string} Event triggered when a cart update fails */
  static cartError = 'cart:error';
  /** @static @constant {string} Event triggered when a product is added to cart */
  static productAdded = 'product:added';
  /** @static @constant {string} Event triggered when a dialog is opened */
  static dialogOpen = 'dialog:open';
  /** @static @constant {string} Event triggered when a dialog is closed */
  static dialogClose = 'dialog:close';
  /** @static @constant {string} Event triggered when filters are updated */
  static filterUpdate = 'filter:update';
  /** @static @constant {string} Event triggered when filter content is moved for mobile */
  static filterContentMove = 'filter:content-move';
  static mediaStartedPlaying = 'media:started-playing';
    // Event triggered when quantity-selector value is changed
  static quantitySelectorUpdate = 'quantity-selector:update';
  /** @static @constant {string} Event triggered when a discount is applied */
  static discountUpdate = 'discount:update';
  /** @static @constant {string} Event triggered when a floating panel is opened */
  static floatingPanelOpened = 'floating-panel:opened';
  /** @static @constant {string} Event triggered when a mega menu submenu is opened */
  static megaMenuSubmenuOpened = 'mega-menu:submenu-opened';
  /** @static @constant {string} Event triggered when a mega menu submenu is closed */
  static megaMenuSubmenuClosed = 'mega-menu:submenu-closed';
  /** @static @constant {string} Event triggered when a mega menu drawer is opened */
  static megaMenuDrawerOpened = 'mega-menu:drawer-opened';
  /** @static @constant {string} Event triggered when a mega menu drawer is closed */
  static megaMenuDrawerClosed = 'mega-menu:drawer-closed';
  /** @static @constant {string} Event triggered when a mega menu drawer page is changed */
  static megaMenuDrawerPageChanged = 'mega-menu:drawer-page-changed';
}

/**
 * Event fired when a variant is selected
 * @extends {Event}
 */
export class VariantSelectedEvent extends Event {
  /**
   * Creates a new VariantSelectedEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The id of the variant
   */
  constructor(resource) {
    super(ThemeEvents.variantSelected, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * Event class for media playback starts
 * @extends {Event}
 */
export class MediaStartedPlayingEvent extends Event {
  /**
   * Creates a new MediaStartedPlayingEvent
   * @param {HTMLElement} resource - The element containing the video that emitted the event
   */
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * Event fired after a variant is updated
 * @extends {Event}
 */
export class VariantUpdateEvent extends Event {
  /**
   * Creates a new VariantUpdateEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The id of the variant
   * @param {boolean} resource.available - Whether the variant is available
   * @param {boolean} resource.inventory_management - Whether the variant has inventory management
   * @param {Object} [resource.featured_media] - The featured media of the variant
   * @param {string} [resource.featured_media.id] - The id of the featured media
   * @param {Object} [resource.featured_media.preview_image] - The preview image of the featured media
   * @param {string} [resource.featured_media.preview_image.src] - The src URL of the preview image
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} data - Additional event data
   * @param {Document} data.html - The new document fragment for the variant
   * @param {string} data.productId - The product ID of the updated variant, used to ensure the correct product form is updated
   * @param {Object} [data.newProduct] - If a new product was loaded as part of the variant update (combined listing)
   * @param {string} data.newProduct.id - The id of the new product
   * @param {string} data.newProduct.url - The url of the new product
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.variantUpdate, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId,
      data: {
        html: data.html,
        productId: data.productId,
        newProduct: data.newProduct,
      },
    };
    
  }
}

/**
 * Event class for cart additions
 * @extends {Event}
 */
export class CartAddEvent extends Event {
  /**
   * Creates a new CartAddEvent
   * @param {Object} [resource] - The new cart object
   * @param {string} [sourceId] - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was added
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(CartAddEvent.eventName, { bubbles: true });
    
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data,
      },
    };
  }

  static eventName = ThemeEvents.cartUpdate;
}

/**
 * Event class for cart updates
 * @extends {Event}
 */
export class CartUpdateEvent extends Event {
  /**
   * Creates a new CartUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was updated
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data,
      },
    };
    
  }
}

/**
 * Event class for cart errors
 * @extends {Event}
 */
export class CartErrorEvent extends Event {
  /**
   * Creates a new CartErrorEvent
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {string} message - A message from the server response
   * @param {Object} description - Description from the server response
   * @param {Object} errors - Errors from the server response
   */
  constructor(sourceId, message, description, errors) {
    super(ThemeEvents.cartError, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        message,
        errors,
        description,
      },
    };
  }
}

/**
 * Event fired when a product is added to cart
 * @extends {Event}
 */
export class ProductAddedEvent extends Event {
  /**
   * Creates a new ProductAddedEvent
   * @param {Object} cartData - The updated cart object after product addition
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {string} [data.productId] - The id of the product that was added
   * @param {string} [data.variantId] - The id of the variant that was added
   * @param {number} [data.quantity] - The quantity that was added
   * @param {string} [data.source] - The source of the product addition (product-form, quick-add, etc.)
   * @param {Record<string, string>} [data.sections] - The sections affected by the product addition
   */
  constructor(cartData, sourceId, data = {}) {
    super(ThemeEvents.productAdded, { bubbles: true });
    this.detail = {
      cart: cartData,
      sourceId,
      data: {
        productId: data.productId,
        variantId: data.variantId,
        quantity: data.quantity,
        source: data.source || 'product-form',
        sections: data.sections,
        ...data,
      },
    };
  }
}

/**
 * Event fired when a dialog opens
 * @extends {Event}
 */
export class DialogOpenEvent extends Event {
  /**
   * Creates a new DialogOpenEvent
   * @param {string} sourceId - The id of the dialog component
   * @param {Object} [data] - Additional event data
   * @param {string} [data.contentUrl] - The URL of the content that was loaded
   * @param {string} [data.contentTemplate] - The template selector that was used
   */
  constructor(sourceId, data = {}) {
    super(ThemeEvents.dialogOpen, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        contentUrl: data.contentUrl,
        contentTemplate: data.contentTemplate,
      },
    };
  }

  static eventName = ThemeEvents.dialogOpen;
}

/**
 * Event fired when a dialog closes
 * @extends {Event}
 */
export class DialogCloseEvent extends Event {
  /**
   * Creates a new DialogCloseEvent
   * @param {string} sourceId - The id of the dialog component
   * @param {Object} [data] - Additional event data
   * @param {string} [data.closeReason] - The reason for closing (escape, outside-click, button)
   */
  constructor(sourceId, data = {}) {
    super(ThemeEvents.dialogClose, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        closeReason: data.closeReason,
      },
    };
  }

  static eventName = ThemeEvents.dialogClose;
}


/**
 * Event fired when filters are updated
 * @extends {Event}
 */
export class FilterUpdateEvent extends Event {
  /**
   * Creates a new FilterUpdateEvent
   * @param {URLSearchParams} resource - The URL parameters representing the current filters
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {string} [data.sectionId] - The section ID that should be updated
   * @param {boolean} [data.isDialog] - Whether the update is happening in a dialog context
   */
  constructor(resource, sourceId, data = {}) {
    super(ThemeEvents.filterUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        sectionId: data.sectionId,
        isDialog: data.isDialog,
        ...data,
      },
    };
  }
}

/**
 * Event fired when filter content is moved for mobile display
 * @extends {Event}
 */
export class FilterContentMoveEvent extends Event {
  /**
   * Creates a new FilterContentMoveEvent
   * @param {'move'|'restore'} action - The action being performed
   * @param {string} sectionId - The section id
   * @param {Object} [data] - Additional event data
   * @param {string} [data.filterLayout] - The filter layout mode
   * @param {boolean} [data.isMobile] - Whether in mobile view
   */
  constructor(action, sectionId, data = {}) {
    super(ThemeEvents.filterContentMove, { bubbles: true });
    this.detail = {
      action,
      sectionId,
      data: {
        filterLayout: data.filterLayout,
        isMobile: data.isMobile,
        ...data,
      },
    };
  }
}
/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class QuantitySelectorUpdateEvent extends Event {
  /**
   * Creates a new QuantitySelectorUpdateEvent
   * @param {number} quantity - Quantity value
   * @param {number} [cartLine] - The id of the updated cart line
   */
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
    this.detail = {
      quantity,
      cartLine,
    };
  }
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class DiscountUpdateEvent extends Event {
  /**
   * Creates a new DiscountUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   */
  constructor(resource, sourceId) {
    super(ThemeEvents.discountUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
    };
  }
}

/**
 * Event fired when a floating panel is opened
 * @extends {Event}
 */
export class FloatingPanelOpenedEvent extends Event {
  /**
   * Creates a new FloatingPanelOpenedEvent
   * @param {HTMLDetailsElement} parentDetails - The parent details element of the opened panel
   */
  constructor(parentDetails) {
    super(ThemeEvents.floatingPanelOpened, { bubbles: true });
    this.detail = {
      parentDetails,
    };
  }
}

/**
 * Event fired when a mega menu submenu is opened
 * @extends {Event}
 */
export class MegaMenuSubmenuOpenedEvent extends Event {
  /**
   * Creates a new MegaMenuSubmenuOpenedEvent
   * @param {string} sourceId - The id of the mega menu component
   * @param {Object} [data] - Additional event data
   * @param {number} [data.submenuKey] - The key of the opened submenu
   * @param {string} [data.submenuTitle] - The title of the opened submenu
   */
  constructor(sourceId, data = {}) {
    super(ThemeEvents.megaMenuSubmenuOpened, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        submenuKey: data.submenuKey,
        submenuTitle: data.submenuTitle,
        ...data,
      },
    };
  }
}

/**
 * Event fired when a mega menu submenu is closed
 * @extends {Event}
 */
export class MegaMenuSubmenuClosedEvent extends Event {
  /**
   * Creates a new MegaMenuSubmenuClosedEvent
   * @param {string} sourceId - The id of the mega menu component
   * @param {Object} [data] - Additional event data
   * @param {number} [data.submenuKey] - The key of the closed submenu
   * @param {string} [data.submenuTitle] - The title of the closed submenu
   */
  constructor(sourceId, data = {}) {
    super(ThemeEvents.megaMenuSubmenuClosed, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        submenuKey: data.submenuKey,
        submenuTitle: data.submenuTitle,
        ...data,
      },
    };
  }
}

/**
 * Event fired when a mega menu drawer is opened
 * @extends {Event}
 */
export class MegaMenuDrawerOpenedEvent extends Event {
  /**
   * Creates a new MegaMenuDrawerOpenedEvent
   * @param {string} sourceId - The id of the mega menu component
   * @param {Object} [data] - Additional event data
   * @param {string} [data.drawerPage] - The initial drawer page opened
   */
  constructor(sourceId, data = {}) {
    super(ThemeEvents.megaMenuDrawerOpened, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        drawerPage: data.drawerPage,
        ...data,
      },
    };
  }
}

/**
 * Event fired when a mega menu drawer is closed
 * @extends {Event}
 */
export class MegaMenuDrawerClosedEvent extends Event {
  /**
   * Creates a new MegaMenuDrawerClosedEvent
   * @param {string} sourceId - The id of the mega menu component
   * @param {Object} [data] - Additional event data
   * @param {string} [data.closeReason] - The reason for closing (button, outside-click, etc.)
   */
  constructor(sourceId, data = {}) {
    super(ThemeEvents.megaMenuDrawerClosed, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        closeReason: data.closeReason,
        ...data,
      },
    };
  }
}

/**
 * Event fired when a mega menu drawer page is changed
 * @extends {Event}
 */
export class MegaMenuDrawerPageChangedEvent extends Event {
  /**
   * Creates a new MegaMenuDrawerPageChangedEvent
   * @param {string} sourceId - The id of the mega menu component
   * @param {Object} [data] - Additional event data
   * @param {string} [data.fromPage] - The previous page
   * @param {string} [data.toPage] - The new page
   * @param {string} [data.pageTitle] - The title of the new page
   */
  constructor(sourceId, data = {}) {
    super(ThemeEvents.megaMenuDrawerPageChanged, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        fromPage: data.fromPage,
        toPage: data.toPage,
        pageTitle: data.pageTitle,
        ...data,
      },
    };
  }
}

/**
 * @typedef {Object} SlideshowSelectEventData
 * @property {number} index
 * @property {string | null} id
 * @property {Element} slide
 * @property {number} previousIndex
 * @property {boolean} userInitiated
 * @property {'select' | 'scroll' | 'drag'} trigger
 */

export class SlideshowSelectEvent extends Event {
  /**  @param {SlideshowSelectEventData} data */
  constructor(data) {
    super(SlideshowSelectEvent.eventName, { bubbles: true });
    this.detail = data;
  }

  /** @type {SlideshowSelectEventData} */
  detail;

  static eventName = 'slideshow:select';
}

